import { Badge, Box, HStack, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { SharedItemKind, type SharedItemDto } from "../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";

interface Props {
  shared: SharedItemDto[];
  documents: { id: string; name: string }[];
  emptyText?: string;
}

export const SharedWithParticipants = ({ shared, documents, emptyText }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  if (shared.length === 0) {
    return emptyText ? <Text color="smMuted" fontSize="sm">{emptyText}</Text> : null;
  }
  const newestFirst = [...shared].sort((a, b) => b.sharedAt.localeCompare(a.sharedAt));
  const label = (kind: SharedItemKind) => {
    switch (kind) {
      case SharedItemKind.Notice:
        return t("Notice");
      case SharedItemKind.Answer:
        return t("Answer to a question");
      case SharedItemKind.Document:
        return t("Document");
    }
  };

  return (
    <Stack spacing="3" data-testid="shared-with-participants">
      {newestFirst.map((item) => (
        <Box key={item.id} borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3">
          <HStack spacing="2" mb="1" wrap="wrap">
            <Badge variant="subtle">{label(item.kind)}</Badge>
            <Text fontSize="xs" color="smMuted">
              {displayDateWithTime(item.sharedAt)}
            </Text>
          </HStack>
          {item.kind === SharedItemKind.Notice && (
            <>
              <Text fontWeight="semibold">{item.title}</Text>
              <Text whiteSpace="pre-wrap" fontSize="sm">
                {item.text}
              </Text>
            </>
          )}
          {item.kind === SharedItemKind.Answer && (
            <>
              <Text fontSize="sm" fontWeight="semibold" whiteSpace="pre-wrap">
                {t("Question: {{text}}", { text: item.question })}
              </Text>
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {item.text}
              </Text>
            </>
          )}
          {item.kind === SharedItemKind.Document && (
            <Text fontSize="sm">
              {t("Added the document «{{name}}»", {
                name: documents.find((document) => document.id === item.documentId)?.name ?? "",
              })}
            </Text>
          )}
        </Box>
      ))}
    </Stack>
  );
};
